/* eslint-disable camelcase */
import { Text, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';
import { typeguards } from '@finley-technologies/valley-bank-utils';

import {
  Maybe,
  useGetValleyBankCreditFacilityLoanProductMechanicsQuery,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { HoldingsTableColumnDef, Collateral, LoanProduct } from '../types';
import { parseBaseFields } from '../utils';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import {
  Alert,
  ProgressAlert,
} from '~/packages/common-frontend/components/Alert';
import { errorToastConfig } from '~/packages/common-frontend/constants';

export const BASE_FIELDS_TO_HIDE = [
  'he_secd_pldg_amt_fx',
  'he_secd_unpldg_amt_us',
  'collateral_description',
  'pre_allocation_lendable_value',
  'post_allocation_lendable_value',
  'hecoll_bsys_id',
  'hecoll_bank_num',
  'hecoll_id',
  'hecoll_collateral_account_number',
  'hecoll_desc',
  'hecoll_current_appraisal',
  'hecoll_type_code',
  'hecoll_pledged_ind',
  'he_secd_bank_num',
  'he_secd_src_system',
  'he_secd_src_system_id',
  'he_secd_unpldg_unit',
  'he_secd_ovrd_flg',
  'he_secd_last_updt',
  'he_secd_last_updt_by',
  'he_secd_adv_rt',
  'he_secd_unit_price_fx',
  'he_secd_frgn_cur_fxr_rate',
  'he_sec_as_of_dt',
  'he_sec_bsys_id',
  'he_sec_bank_num',
  'he_sec_country_key',
  'he_sec_unit_type',
  'he_sec_ticker_symbol',
  'he_sec_short_name',
  'he_sec_issuer',
  'he_sec_price_date',
  'he_sec_unit_price_us',
  'he_sec_exchange',
  'he_sec_rating_src',
  'he_sec_issue_dt',
  'he_sec_redemp_dt',
  'he_sec_int_start',
  'he_sec_coupon_per_yr',
  'he_sec_int_type',
  'he_sec_asset_class_code',
  'he_sec_segment_code',
  'he_sec_strategy_code',
  'he_sec_long_desc',
  'he_par_logic',
  'fund_strategy',
  'fund_rtg_class_focus',
  'fund_geo_focus',
  'hb_oas_eff_durtn',
  'yas_mod_dur',
  'arrangement_group',
  'collateral_account_number',
  'collateral_account_type',
  'reporting_date',
  'loan_account_number',
  'hedeposit_bsys_id',
  'hedeposit_acct_num',
  'hedeposit_acct_type',
  'hedeposit_cur_bal',
  'hedeposit_avg_bal',
  'hedeposit_nsf_tot',
  'hedeposit_last_nsf_dt',
  'hedeposit_last_nsf_amt',
  'hedeposit_last_activity_dt',
  'hedeposit_last_deposit_dt',
  'hedeposit_tot_nsf_checks_paid_ytd',
  'hedeposit_acct_open_dt',
  'hedeposit_branch_num',
  'hedeposit_acct_status',
  'hedeposit_acct_name',
  'hedeposit_branch_desc',
  'hedeposit_frgn_currency_code',
  'hedeposit_frgn_cur_amt',
  'hedeposit_frgn_cur_xfr_rate',
  'hedeposit_frgn_avail_bal',
  'hedeposit_frgn_orig_amt',
  'hedeposit_avail_bal',
  'hecoll_account_hold_amount',
  'hecoll_file_date',
  'hecoll_deposit_acct_type_id',
  'hecoll_deposit_account_num',
  'hecoll_deposit_balance',
  'hecoll_maturity_dt',
  'hecoll_frgn_currency_ind',
  'hecoll_frgn_currency_code',
  'hecoll_frgn_cur_amt',
  'hecoll_frgn_cur_xfr_rate',
  'hecoll_source_system',
  'heloan_as_of_dt',
  'heloan_bsys_id',
  'heloan_acct_num',
  'helcrel_rel_type_id',
  'helcrel_acct_type',
  'helcrel_portfolio_number',
  // new columns to hide 2024-10-22 according to Stacey
  'he_secd_pldg_amt_us', // Secured Pldg Amg
  'he_secd_unpldg_amt_fx', // FX Amount
  'hecoll_desc_2', // Source
  'he_secd_as_of_dt', // Date
  'he_secd_pldg_unit', // Pldg Unit
  'he_secd_unit_price_us', // Unit Value
  'isin', // ISN
  'market_sector_des', // Market Sector
  'eqy_fund_ind', // Fund Strategy
  'fund_objective_long', // Fund Objective
  'product_code', // Product Code
  'he_sec_issuer_country', // Country
  'fund_asset_class_focus', // Fund Asset Class Focus
];

const BASE_FIELDS_TO_RENAME = {
  account_number: 'Account Number',
  advance_rate_reason: 'Advance Rate Reason',
  advance_rate: 'Advance Rate',
  cif_num: 'Customer Number',
  collateral_market_value: 'Market Value',
  collateral_type: 'Collateral Type',
  currency_code: 'Currency',
  eqy_fund_ind: 'Fund Strategy',
  fund_asset_class_focus: 'Fund Asset Class Focus',
  fund_objective_long: 'Fund Objective',
  he_sec_asset_class: 'Asset Class',
  he_sec_cusip: 'Security Cusip',
  he_sec_issuer_country: 'Country',
  he_sec_mat_dt: 'Maturity',
  he_sec_rating: 'Rating',
  he_sec_sec_desc: 'Security Description',
  he_sec_security_num: 'Security Number',
  he_sec_segment: 'Sec Segment',
  he_sec_strategy: 'Sec Strategy',
  he_secd_as_of_dt: 'Date',
  he_secd_pldg_amt_us: 'Secured Pldg Amt',
  he_secd_pldg_yn: 'Pledged',
  he_secd_pldg_unit: 'Pldg Unit',
  he_secd_unit_price_us: 'Unit Value',
  he_secd_unpldg_amt_fx: 'FX Amount',
  heccrel_portfolio_number: 'Arrangement Group',
  hecoll_desc_2: 'Source',
  isin: 'ISIN',
  loan_account: 'Loan Number',
  market_sector_des: 'Market Sector',
  pre_allocation_lendable_value: 'Lendable Value',
  product_name: 'Product Name',
  product_code: 'Product Code',
};

type BaseFieldToRename = keyof typeof BASE_FIELDS_TO_RENAME;

// this list needs to be updated if their product names change in the credit agreement, otherwise the column reordering functionality wont' work
export const ALL_BASE_FIELDS = [
  'baseField-hecoll_bsys_id-securities',
  'baseField-hecoll_bank_num-securities',
  'baseField-hecoll_id-securities',
  'baseField-hecoll_collateral_account_number-securities',
  'baseField-hecoll_desc-securities',
  'baseField-hecoll_desc_2-securities',
  'baseField-hecoll_current_appraisal-securities',
  'baseField-hecoll_type_code-securities',
  'baseField-hecoll_pledged_ind-securities',
  'baseField-heccrel_portfolio_number-securities',
  'baseField-he_sec_security_num-securities',
  'baseField-he_secd_as_of_dt-securities',
  'baseField-he_secd_bank_num-securities',
  'baseField-he_secd_src_system-securities',
  'baseField-he_secd_src_system_id-securities',
  'baseField-he_secd_pldg_yn-securities',
  'baseField-he_secd_pldg_unit-securities',
  'baseField-he_secd_pldg_amt_us-securities',
  'baseField-he_secd_pldg_amt_fx-securities',
  'baseField-he_secd_unpldg_unit-securities',
  'baseField-he_secd_unpldg_amt_us-securities',
  'baseField-he_secd_unpldg_amt_fx-securities',
  'baseField-he_secd_ovrd_flg-securities',
  'baseField-he_secd_last_updt-securities',
  'baseField-he_secd_last_updt_by-securities',
  'baseField-he_secd_adv_rt-securities',
  'baseField-he_secd_unit_price_fx-securities',
  'baseField-he_secd_unit_price_us-securities',
  'baseField-he_secd_frgn_cur_fxr_rate-securities',
  'baseField-currency_code-securities',
  'baseField-he_sec_cusip-securities',
  'baseField-he_sec_as_of_dt-securities',
  'baseField-he_sec_bsys_id-securities',
  'baseField-he_sec_bank_num-securities',
  'baseField-he_sec_country_key-securities',
  'baseField-he_sec_sec_desc-securities',
  'baseField-he_sec_unit_type-securities',
  'baseField-he_sec_ticker_symbol-securities',
  'baseField-he_sec_short_name-securities',
  'baseField-he_sec_issuer-securities',
  'baseField-he_sec_issuer_country-securities',
  'baseField-he_sec_asset_class-securities',
  'baseField-he_sec_price_date-securities',
  'baseField-he_sec_unit_price_us-securities',
  'baseField-he_sec_exchange-securities',
  'baseField-he_sec_rating-securities',
  'baseField-he_sec_rating_src-securities',
  'baseField-he_sec_issue_dt-securities',
  'baseField-he_sec_mat_dt-securities',
  'baseField-he_sec_redemp_dt-securities',
  'baseField-he_sec_int_start-securities',
  'baseField-he_sec_coupon_per_yr-securities',
  'baseField-he_sec_int_type-securities',
  'baseField-he_sec_asset_class_code-securities',
  'baseField-he_sec_segment_code-securities',
  'baseField-he_sec_strategy_code-securities',
  'baseField-he_sec_long_desc-securities',
  'baseField-he_sec_strategy-securities',
  'baseField-he_sec_segment-securities',
  'baseField-he_par_logic-securities',
  'baseField-isin-securities',
  'baseField-market_sector_des-securities',
  'baseField-fund_strategy-securities',
  'baseField-eqy_fund_ind-securities',
  'baseField-fund_asset_class_focus-securities',
  'baseField-fund_rtg_class_focus-securities',
  'baseField-fund_geo_focus-securities',
  'baseField-fund_objective_long-securities',
  'baseField-hb_oas_eff_durtn-securities',
  'baseField-yas_mod_dur-securities',
  'baseField-collateral_account_type-securities',
  'baseField-reporting_date-securities',
  'baseField-hedeposit_bsys_id-deposits',
  'baseField-hedeposit_acct_num-deposits',
  'baseField-hedeposit_acct_type-deposits',
  'baseField-hedeposit_cur_bal-deposits',
  'baseField-hedeposit_avg_bal-deposits',
  'baseField-hedeposit_nsf_tot-deposits',
  'baseField-hedeposit_last_nsf_dt-deposits',
  'baseField-hedeposit_last_nsf_amt-deposits',
  'baseField-hedeposit_last_activity_dt-deposits',
  'baseField-hedeposit_last_deposit_dt-deposits',
  'baseField-hedeposit_tot_nsf_checks_paid_ytd-deposits',
  'baseField-hedeposit_acct_open_dt-deposits',
  'baseField-hedeposit_branch_num-deposits',
  'baseField-hedeposit_acct_status-deposits',
  'baseField-hedeposit_acct_name-deposits',
  'baseField-hedeposit_branch_desc-deposits',
  'baseField-hedeposit_frgn_currency_code-deposits',
  'baseField-hedeposit_frgn_cur_amt-deposits',
  'baseField-hedeposit_frgn_cur_xfr_rate-deposits',
  'baseField-hedeposit_frgn_avail_bal-deposits',
  'baseField-hedeposit_frgn_orig_amt-deposits',
  'baseField-hedeposit_avail_bal-deposits',
  'baseField-hecoll_id-deposits',
  'baseField-hecoll_bsys_id-deposits',
  'baseField-hecoll_desc-deposits',
  'baseField-hecoll_account_hold_amount-deposits',
  'baseField-hecoll_file_date-deposits',
  'baseField-hecoll_current_appraisal-deposits',
  'baseField-hecoll_type_code-deposits',
  'baseField-hecoll_deposit_acct_type_id-deposits',
  'baseField-hecoll_deposit_account_num-deposits',
  'baseField-hecoll_deposit_balance-deposits',
  'baseField-hecoll_maturity_dt-deposits',
  'baseField-hecoll_frgn_currency_ind-deposits',
  'baseField-hecoll_frgn_currency_code-deposits',
  'baseField-hecoll_frgn_cur_amt-deposits',
  'baseField-hecoll_frgn_cur_xfr_rate-deposits',
  'baseField-hecoll_source_system-deposits',
  'baseField-hecoll_pledged_ind-deposits',
  'baseField-heloan_as_of_dt-deposits',
  'baseField-heloan_bsys_id-deposits',
  'baseField-heloan_acct_num-deposits',
  'baseField-helcrel_rel_type_id-deposits',
  'baseField-helcrel_acct_type-deposits',
  'baseField-helcrel_portfolio_number-deposits',
  'baseField-collateral_account_type-deposits',
  'baseField-reporting_date-deposits',
  'baseField-product_code-deposits',
];

export const isBaseFieldToRename = (
  field: string,
): field is BaseFieldToRename =>
  Object.keys(BASE_FIELDS_TO_RENAME).includes(field);

export const getHeader = (field: string) => {
  const snakeCaseField = _.snakeCase(field);
  return isBaseFieldToRename(snakeCaseField)
    ? BASE_FIELDS_TO_RENAME[snakeCaseField]
    : field;
};

export const isInitiallyVisible = (field: string) =>
  !BASE_FIELDS_TO_HIDE.includes(field);

const LoadingAlert = () => (
  <ProgressAlert status="info">
    <Text> Loading your portfolio mechanics. This will take a moment.</Text>
  </ProgressAlert>
);

const ErrorAlert = () => (
  <Alert status="error">
    <Text>
      There was an error loading your portfolio mechanics. Please try again.
    </Text>
  </Alert>
);

const transformBaseFieldConfigToColumnDef = ({
  loanProductId,
  loanProductName,
  baseFieldConfig: { field },
}: {
  loanProductId: string;
  loanProductName: string;
  baseFieldConfig: CreditAgreement.DataTapeField.BaseConfig;
}): HoldingsTableColumnDef => {
  return {
    id: `baseField-${field}-${loanProductName.toLowerCase()}`,
    header: getHeader(field),
    accessorFn: ({ receivable }: Collateral) => {
      if (receivable.finley_loan_product_id === loanProductId) {
        return receivable.receivableReponseTapeRow.baseFields[field] || '-';
      }
      return 'N/A';
    },
    meta: {
      loanProductId,
      loanProductName,
      initialVisibility: isInitiallyVisible(field),
    },
  };
};

const loanProductIdToName = (
  loanProductId: Maybe<string>,
  loanProducts: LoanProduct[],
) =>
  loanProducts.find(
    (loanProduct) => loanProduct.loanProductId === loanProductId,
  )?.loanProductName ||
  loanProductId ||
  '-';

export const useBaseFieldColumnDefinitions = (isWip?: boolean) => {
  const toast = useToast();
  const [columnDefs, setColumnDefs] = useState<HoldingsTableColumnDef[]>([]);
  const [loanProducts, setLoanProducts] = useState<LoanProduct[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const {
    error: isError,
  } = useGetValleyBankCreditFacilityLoanProductMechanicsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      isWip,
    },
    onCompleted: (res) => {
      // @jendevelops do we have to be wary of MCF here?
      const creditFacility = res.user?.organization.creditFacilities[0];
      const newLoanProducts =
        creditFacility?.borrowingBaseMechanicsForLoanProducts.reduce(
          (acc, { loanProductId, loanProductName }) => {
            acc.push({ loanProductId, loanProductName });
            return acc;
          },
          [] as LoanProduct[],
        ) || [];
      const baseFieldConfigs =
        creditFacility?.borrowingBaseMechanicsForLoanProducts.flatMap(
          ({ baseFields, loanProductId, loanProductName }) => {
            const parsedBaseFields = parseBaseFields({
              baseFieldsJson: baseFields,
              loanProductId,
              creditFacilitySlug: creditFacility.slug,
              isWip,
            });
            return parsedBaseFields
              .filter(
                (baseFieldConfig) =>
                  !typeguards.isValleyBankRequiredBaseFields(
                    baseFieldConfig.field,
                  ), // remove finley prefixed basefields
              )
              .map((baseFieldConfig) => ({
                baseFieldConfig,
                loanProductId,
                loanProductName,
              }));
          },
        ) || [];
      const newColumnDefs = baseFieldConfigs.map(
        ({ baseFieldConfig, loanProductId, loanProductName }) =>
          transformBaseFieldConfigToColumnDef({
            loanProductId,
            loanProductName,
            baseFieldConfig,
          }),
      );
      setLoanProducts(newLoanProducts);
      setColumnDefs(newColumnDefs);
      setIsLoading(false);
    },
  });

  const _LoadingAlert = useCallback(
    () => (isLoading ? <LoadingAlert /> : null),
    [isLoading],
  );

  const _ErrorAlert = useCallback(() => (isError ? <ErrorAlert /> : null), [
    isError,
  ]);

  const _loanProductToName = useCallback(
    (loanProductId: Maybe<string>) => {
      return loanProductIdToName(loanProductId, loanProducts);
    },
    [loanProducts],
  );

  return {
    columnDefs,
    isLoading,
    isError,
    LoadingAlert: _LoadingAlert,
    ErrorAlert: _ErrorAlert,
    loanProducts,
    loanProductToName: _loanProductToName,
  };
};
